// UserForm.js
import React, { useRef, useState, useEffect } from 'react';
import { Button, Form, FloatingLabel, Spinner, Alert } from 'react-bootstrap';
import { httpsCallable } from 'firebase/functions';
import { functions, db } from '../firebaseConfig';
import { useFirestore } from '../contexts/FirestoreContext';
import { doc, updateDoc, deleteDoc } from 'firebase/firestore';
import Select from 'react-select';

function UserForm({ onClose, user, userRole, request }) {
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const [customers, setCustomers] = useState([]);
  const [upfitters, setUpfitters] = useState([]);
  const [selectedUpfitter, setSelectedUpfitter] = useState('None');
  const [selectedRole, setSelectedRole] = useState('Customer');
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [customerSuggestions, setCustomerSuggestions] = useState([]);
  const [mustChangePassword, setMustChangePassword] = useState(false);

  // NEW: Bill-Out Approver flag
  const [isBillOutApprover, setIsBillOutApprover] = useState(false);

  const { getData } = useFirestore();

  const nameRef = useRef();
  const emailRef = useRef();
  const passwordRef = useRef();

  // Free-text customer/company from request (if any)
  const requestedCustomerText = request?.company || null;
  const autoMatchedCustomersRef = useRef(false);

  useEffect(() => {
    const fetchData = async () => {
      const customerData = await getData('customers');
      const upfitterData = await getData('Upfitters');
      setCustomers(customerData);
      setUpfitters(upfitterData);

      if (user) {
        // edit flow
        if (nameRef.current) nameRef.current.value = user.name || '';
        if (emailRef.current) emailRef.current.value = user.email || '';
        setSelectedCustomers(user.customers || []);
        setSelectedUpfitter(user.upfitter || 'None');
        setSelectedRole(user.role || 'Customer');
        setMustChangePassword(!!user.changePassword);

        // NEW: prefill Bill-Out Approver
        setIsBillOutApprover(!!user.billOutApprover);

      } else if (request) {
        // create-from-request flow (prefill)
        if (nameRef.current) nameRef.current.value = request.fullName || request.name || '';
        if (emailRef.current) emailRef.current.value = request.email || '';
        setSelectedRole('Customer');
        setSelectedCustomers([]);
        setSelectedUpfitter('None');
        setMustChangePassword(false);

        // NEW: default false for new users
        setIsBillOutApprover(false);
      } else {
        // brand-new user (no request)
        setIsBillOutApprover(false);
      }
    };
    fetchData();
  }, [getData, user, request]);

  useEffect(() => {
    autoMatchedCustomersRef.current = false;
  }, [request?.id]);

  useEffect(() => {
    if (user || !request?.company) {
      setCustomerSuggestions([]);
      return;
    }
    if (!customers.length) return;

    const companyInput = request.company.trim().toLowerCase();
    if (!companyInput) {
      setCustomerSuggestions([]);
      return;
    }

    const matches = customers
      .filter((cust) => cust.strCustName?.toLowerCase().includes(companyInput))
      .sort((a, b) => a.strCustName.localeCompare(b.strCustName));

    setCustomerSuggestions(matches.slice(0, 6));

    if (!autoMatchedCustomersRef.current && selectedCustomers.length === 0) {
      const exactMatches = matches.filter(
        (cust) => cust.strCustName?.toLowerCase() === companyInput
      );
      if (exactMatches.length === 1) {
        setSelectedCustomers([exactMatches[0].strCustNbr]);
        autoMatchedCustomersRef.current = true;
      }
    }
  }, [customers, request, user, selectedCustomers.length]);

  const toggleSuggestedCustomer = (custNbr) => {
    setSelectedCustomers((prev) =>
      prev.includes(custNbr)
        ? prev.filter((id) => id !== custNbr)
        : [...prev, custNbr]
    );
  };

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      console.log("User: ", user)
      if (user) {
        // ===== Edit existing user
        const updatedUserDoc = {
          ...user,
          name: nameRef.current.value,
          email: emailRef.current.value,
          customers: selectedCustomers,
          upfitter: selectedUpfitter,
          role: selectedRole,
          changePassword: mustChangePassword,

          // NEW: persist to Firestore
          billOutApprover: isBillOutApprover,
        };

        console.log("updatedUserDoc: ", updatedUserDoc)

        const userRef = doc(db, 'users', user.id);
        await updateDoc(userRef, updatedUserDoc);

        // Optionally update auth/claims via callable
        const claimPayload = {
          uid: user.id,
          mustChangePassword: !!mustChangePassword,
        };
        const newPassword = passwordRef.current?.value;
        if (newPassword) claimPayload.password = newPassword;
        if (selectedRole) claimPayload.role = selectedRole;

        // NEW: pass through to function (if your function handles it)
        claimPayload.billOutApprover = !!isBillOutApprover;

        const updateUser = httpsCallable(functions, 'updateUser');
        await updateUser(claimPayload);

        console.log("updateUser: ", updateUser)

      } else {
        // ===== Add new user (optionally from a request)
        const createUser = httpsCallable(functions, 'createUser');
        await createUser({
          email: emailRef.current.value,
          password: passwordRef.current.value,
          mustChangePassword,
          name: nameRef.current.value,
          customers: selectedCustomers,
          upfitter: selectedUpfitter,
          role: selectedRole,

          // NEW: include bill-out approver in creation
          billOutApprover: !!isBillOutApprover,
        });

        // Remove the request doc after successful creation
        if (request?.id) {
          await deleteDoc(doc(db, 'accountRequests', request.id));
        }
      }

      onClose();
    } catch (err) {
      console.error(err);
      setError('Failed to save user: ' + (err?.message || err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="card mt-4">
      <div className="card-body">
        {error && <div className="alert alert-danger">{error}</div>}

        <Form onSubmit={handleSubmit}>
          <fieldset disabled={loading}>
            <Form.Group className="mb-3" controlId="name">
              <FloatingLabel controlId="floatingName" label="Name">
                <Form.Control type="text" ref={nameRef} required />
              </FloatingLabel>
            </Form.Group>

            <Form.Group className="mb-3" controlId="email">
              <FloatingLabel controlId="floatingEmail" label="Email">
                <Form.Control type="email" ref={emailRef} required />
              </FloatingLabel>
            </Form.Group>

            {!user && (
              <Form.Group className="mb-3" controlId="password">
                <FloatingLabel controlId="floatingPassword" label="Password">
                  <Form.Control type="password" ref={passwordRef} required />
                </FloatingLabel>
                <Form.Check
                  type="checkbox"
                  label="User Must Change Password on Next Login"
                  checked={mustChangePassword}
                  onChange={(e) => setMustChangePassword(e.target.checked)}
                />
              </Form.Group>
            )}

            {user && (
              <Form.Group className="mb-3" controlId="newPassword">
                <FloatingLabel controlId="floatingNewPassword" label="New Password (Optional)">
                  <Form.Control type="password" ref={passwordRef} />
                </FloatingLabel>
                <Form.Check
                  type="checkbox"
                  label="User Must Change Password on Next Login"
                  checked={mustChangePassword}
                  onChange={(e) => setMustChangePassword(e.target.checked)}
                />
              </Form.Group>
            )}

            {/* Show request’s free-text customer/company, if present */}
            {requestedCustomerText && !user && (
              <Alert variant="warning" className="mb-3">
                <strong>Requested Customer:</strong> {requestedCustomerText}
                <div className="mb-0 small text-muted">Select the correct customer(s) below.</div>
                {customerSuggestions.length > 0 ? (
                  <>
                    <div className="mt-2 fw-semibold small text-uppercase">
                      Suggested matches
                    </div>
                    <div className="d-flex flex-wrap gap-2 mt-1">
                      {customerSuggestions.map((cust) => {
                        const isSelected = selectedCustomers.includes(cust.strCustNbr);
                        return (
                          <Button
                            key={cust.strCustNbr}
                            size="sm"
                            variant={isSelected ? 'success' : 'outline-primary'}
                            onClick={() => toggleSuggestedCustomer(cust.strCustNbr)}
                          >
                            {cust.strCustName}
                          </Button>
                        );
                      })}
                    </div>
                  </>
                ) : (
                  <div className="mt-2 small text-muted">
                    No direct matches found—use the search below to map the correct customer.
                  </div>
                )}
              </Alert>
            )}

            <Form.Group className="mb-3" controlId="customer">
              <Form.Label>Customers</Form.Label>
              <Select
                isMulti
                value={customers
                  .filter(cust => selectedCustomers.includes(cust.strCustNbr))
                  .map(cust => ({ value: cust.strCustNbr, label: cust.strCustName }))}
                onChange={(opts) => setSelectedCustomers(opts ? opts.map(o => o.value) : [])}
                options={customers.map(cust => ({ value: cust.strCustNbr, label: cust.strCustName }))}
                styles={{
                  control: (p) => ({ ...p, zIndex: 9999 }),
                  menu: (p) => ({ ...p, zIndex: 9999 }),
                }}
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="upfitter">
              <FloatingLabel controlId="floatingUpfitter" label="Upfitter">
                <Form.Select value={selectedUpfitter} onChange={(e) => setSelectedUpfitter(e.target.value)}>
                  {upfitters.map(upfitter => (
                    <option key={upfitter.strUpfitterNbr} value={upfitter.strUpfitterNbr}>
                      {upfitter.strUpfitterName}
                    </option>
                  ))}
                  <option value="None">None</option>
                </Form.Select>
              </FloatingLabel>
            </Form.Group>

            <Form.Group className="mb-3" controlId="role">
              <FloatingLabel controlId="floatingRole" label="Role">
                <Form.Select
                  value={selectedRole}
                  onChange={(e) => setSelectedRole(e.target.value)}
                  disabled={userRole === 'BDC'}
                >
                  <option value="Admin">Admin</option>
                  <option value="PDI">PDI</option>
                  <option value="Sales Manager">Sales Manager</option>
                  <option value="Sales Rep">Sales Rep</option>
                  <option value="BDC">BDC</option>
                  <option value="Customer">Customer</option>
                  <option value="Upfitter">Upfitter</option>
                </Form.Select>
              </FloatingLabel>
            </Form.Group>

            {/* NEW: Bill-Out Approver toggle */}
            <Form.Group className="mb-4" controlId="billOutApprover">
              <Form.Check
                type="checkbox"
                label="Bill-Out Approver"
                checked={isBillOutApprover}
                onChange={(e) => setIsBillOutApprover(e.target.checked)}
              />
              <div className="form-text">
                Can approve Bill Out sheets / totals.
              </div>
            </Form.Group>

            <Button variant="primary" type="submit" disabled={loading}>
              {loading ? (
                <>
                  <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />{' '}
                  {user ? 'Updating...' : 'Adding...'}
                </>
              ) : user ? 'Update User' : 'Add User'}
            </Button>
          </fieldset>
        </Form>
      </div>
    </div>
  );
}

export default UserForm;
